<template>
  <div>
    <h3 class="mb-1 mb-sm-2 mt-4">
      {{ $t("signup.contact_review_component.generator_owner_title") }}
    </h3>
    <p class="mb-2 mb-sm-4">
      {{ $t("signup.contact_review_component.generator_owner_subtext") }}
    </p>

    <!-- Company Search (initial view if fields are pristine) -->
    <template v-if="showSearchUI">
      <!-- Slot for Country Specific QuickFill Component -->
      <slot name="quickfill" :handleSelect="handleQuickFill"></slot>
      <div class="text-center mt-4">
        <button
          type="button"
          class="btn btn-link text-decoration-none"
          @click="showCompanyForm"
        >
          <font-awesome-icon icon="pencil-alt" class="me-1" />
          {{ $t("signup.fi.quickfill.manual_entry") }}
        </button>
      </div>
    </template>

    <!-- Company Form (after selection or manual entry) -->
    <template v-else>
      <div class="mb-6 d-flex flex-column gap-2 flex-grow-1">
        <!-- Search state (shown when quickfill button is clicked) -->
        <div v-if="showQuickFill" class="p-3 pb-4 bg-light rounded">
          <div class="d-flex justify-content-between align-items-baseline mb-2">
            <p class="text-muted fw-semibold mb-0">
              <font-awesome-icon icon="search" class="me-1" />
              {{ $t("signup.fi.quickfill.search") }}
            </p>
            <button
              class="btn btn btn-link ms-auto p-2"
              @click="showQuickFill = false"
            >
              <font-awesome-icon icon="times" class="fs-8" />
            </button>
          </div>
          <!-- Slot for Country Specific QuickFill Component -->
          <slot name="quickfill" :handleSelect="handleQuickFill"></slot>
        </div>

        <div v-else>
          <button
            type="button"
            class="btn btn-link p-0"
            @click="showCompanySearch"
          >
            <font-awesome-icon icon="search" class="me-1" />
            {{ $t("signup.fi.quickfill.search") }}
          </button>
        </div>
      </div>

      <!-- Company form fields -->
      <div v-for="field in fields" :key="field.id">
        <div class="row mb-2 g-2">
          <label :for="field.id" class="col-sm-3 mt-sm-3 col-form-label">{{
            field.label
          }}</label>
          <div class="col-sm-8 col-10">
            <input
              :type="field.type"
              :id="field.id"
              class="form-control"
              :class="{
                'is-invalid': v$[field.model]?.$error,
              }"
              v-model="company[field.model]"
              @blur="v$[field.model]?.$touch()"
            />
            <div v-if="v$[field.model]?.$errors">
              <div
                class="invalid-feedback"
                v-for="error of v$[field.model]?.$errors || []"
                :key="error.$uid"
              >
                {{ error.$message }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { Company, ContactFieldType } from "@/types";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { BaseValidation } from "@vuelidate/core";
import { onMounted, ref, watch } from "vue";

const company = defineModel<Company>({ required: true });

const props = defineProps<{
  fields: ContactFieldType[];
  v$: BaseValidation;
  isDataPristine: boolean;
}>();

const emit = defineEmits<{
  "quickfill-select": [selectedCompanyData: any];
}>();

const showQuickFill = ref(false);
const showSearchUI = ref(props.isDataPristine);

// Show manual form instead of search
const showCompanyForm = () => {
  showSearchUI.value = false;
  showQuickFill.value = false;
};

// Show search quickfill from form view
const showCompanySearch = () => {
  showQuickFill.value = true;
};

const handleQuickFill = (selectedCompanyData: any) => {
  emit("quickfill-select", selectedCompanyData);
  showCompanyForm(); // Switch to form view after selection
};

// Watch the pristine prop to update initial state if needed
watch(
  () => props.isDataPristine,
  (newVal: boolean) => {
    showSearchUI.value = newVal;
  }
);

onMounted(() => {
  // Initial state based on prop
  showSearchUI.value = props.isDataPristine;
});
</script>

<style scoped lang="scss">
/* Reuse styles from AccountFormFields where applicable or define specific ones */
.col-form-label {
  text-align: left;
  font-weight: bold;
  padding: 0;
}
.invalid-feedback {
  display: block;
}
.row {
  margin-left: 0;
  margin-right: 0;
}

@media (max-width: 575.98px) {
  .col-form-label {
    margin-top: 1rem;
  }
}

.bg-light {
  /* Consider defining a variable or using Bootstrap's default */
  background-color: #f8f9fa !important;
}
</style>
